"use client"

import * as React from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { format } from "date-fns"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/modules/shared/ui/components/button"
import { Calendar } from "@/modules/shared/ui/components/calendar"

type CalendarProps = React.ComponentProps<typeof Calendar>

const DEFAULT_START_YEAR = 1900
const DEFAULT_YEARS_PER_PAGE = 12

export type ZoomableCalendarProps = CalendarProps & {
  startYear?: number
  endYear?: number
  yearsPerPage?: number
  /** Popover open state; on close the view resets to the day grid. */
  open?: boolean
}

type View = "day" | "month" | "year"

function firstOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function selectedDate(props: ZoomableCalendarProps): Date | undefined {
  const value = (props as { selected?: unknown }).selected
  return value instanceof Date ? value : undefined
}

function resolveInitialMonth(props: ZoomableCalendarProps): Date {
  if (props.month instanceof Date) return firstOfMonth(props.month)
  if (props.defaultMonth instanceof Date)
    return firstOfMonth(props.defaultMonth)
  const selected = selectedDate(props)
  if (selected) return firstOfMonth(selected)
  return firstOfMonth(new Date())
}

export function ZoomableCalendar(props: ZoomableCalendarProps) {
  const {
    startYear,
    endYear,
    yearsPerPage = DEFAULT_YEARS_PER_PAGE,
    open,
    className,
    classNames,
    styles,
    onMonthChange,
    startMonth,
    endMonth,
    components,
    ...calendarProps
  } = props

  const today = React.useMemo(() => new Date(), [])

  const minYear = Math.max(
    startYear ?? DEFAULT_START_YEAR,
    startMonth?.getFullYear() ?? Number.NEGATIVE_INFINITY,
  )
  const maxYear = Math.min(
    endYear ?? today.getFullYear() + 10,
    endMonth?.getFullYear() ?? Number.POSITIVE_INFINITY,
  )

  const pageFor = React.useCallback(
    (year: number) =>
      minYear + Math.floor((year - minYear) / yearsPerPage) * yearsPerPage,
    [minYear, yearsPerPage],
  )

  const [view, setView] = React.useState<View>("day")
  const [displayMonth, setDisplayMonth] = React.useState<Date>(() =>
    resolveInitialMonth(props),
  )
  const [yearPageStart, setYearPageStart] = React.useState<number>(() =>
    pageFor(resolveInitialMonth(props).getFullYear()),
  )

  React.useEffect(() => {
    if (props.month instanceof Date) {
      setDisplayMonth(firstOfMonth(props.month))
    }
  }, [props.month])

  React.useEffect(() => {
    if (open === false) setView("day")
  }, [open])

  const gridRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (view === "day") return
    const node =
      gridRef.current?.querySelector<HTMLButtonElement>(
        '[data-active="true"]:not(:disabled)',
      ) ??
      gridRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")
    node?.focus()
  }, [view])

  const changeMonth = React.useCallback(
    (next: Date) => {
      const normalised = firstOfMonth(next)
      setDisplayMonth(normalised)
      onMonthChange?.(normalised)
    },
    [onMonthChange],
  )

  const CaptionLabelButton = React.useMemo(() => {
    const triggerClass = cn(
      buttonVariants({ variant: "ghost", size: "sm" }),
      "h-7 px-1.5 text-sm font-medium",
    )
    return function CaptionLabelButton({
      className: labelClassName,
    }: React.HTMLAttributes<HTMLSpanElement>) {
      const captionYear = displayMonth.getFullYear()
      return (
        <span
          className={cn(
            "relative z-20 flex items-center gap-0.5",
            labelClassName,
          )}
        >
          <button
            type="button"
            onClick={() => setView("month")}
            title="Change month"
            className={triggerClass}
          >
            {format(displayMonth, "MMMM")}
          </button>
          <button
            type="button"
            onClick={() => {
              setYearPageStart(pageFor(captionYear))
              setView("year")
            }}
            title="Change year"
            className={triggerClass}
          >
            {captionYear}
          </button>
        </span>
      )
    }
  }, [displayMonth, pageFor])

  const year = displayMonth.getFullYear()

  const monthDisabled = React.useCallback(
    (monthIndex: number) => {
      const monthStart = new Date(year, monthIndex, 1)
      const monthEnd = new Date(year, monthIndex + 1, 0)
      if (startMonth && monthEnd < firstOfMonth(startMonth)) return true
      if (endMonth && monthStart > endMonth) return true
      if (year < minYear || year > maxYear) return true
      return false
    },
    [year, startMonth, endMonth, minYear, maxYear],
  )

  const panelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation()
      setView((v) => (v === "year" ? "month" : "day"))
    }
  }

  if (view === "day") {
    return (
      <Calendar
        {...calendarProps}
        className={className}
        classNames={classNames}
        styles={styles}
        month={displayMonth}
        onMonthChange={changeMonth}
        startMonth={startMonth ?? new Date(minYear, 0, 1)}
        endMonth={endMonth ?? new Date(maxYear, 11, 1)}
        components={{ ...components, CaptionLabel: CaptionLabelButton }}
      />
    )
  }

  const pageEnd = Math.min(yearPageStart + yearsPerPage - 1, maxYear)
  const selected = selectedDate(props)

  return (
    <div
      ref={gridRef}
      onKeyDown={panelKeyDown}
      data-slot="zoomable-calendar-panel"
      className={cn(
        "bg-background w-64 p-3 [[data-slot=popover-content]_&]:bg-transparent [[data-slot=card-content]_&]:bg-transparent",
        className,
      )}
    >
      {view === "month" ? (
        <>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Previous year"
              disabled={year <= minYear}
              onClick={() =>
                changeMonth(new Date(year - 1, displayMonth.getMonth(), 1))
              }
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <button
              type="button"
              onClick={() => {
                setYearPageStart(pageFor(year))
                setView("year")
              }}
              aria-label={`${year}, change year`}
              className={cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "h-7 px-2 text-sm font-medium",
              )}
            >
              {year}
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Next year"
              disabled={year >= maxYear}
              onClick={() =>
                changeMonth(new Date(year + 1, displayMonth.getMonth(), 1))
              }
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {Array.from({ length: 12 }, (_, monthIndex) => {
              const isToday =
                year === today.getFullYear() && monthIndex === today.getMonth()
              const isSelected =
                selected != null &&
                selected.getFullYear() === year &&
                selected.getMonth() === monthIndex
              return (
                <Button
                  key={monthIndex}
                  type="button"
                  variant={
                    isSelected ? "default" : isToday ? "secondary" : "ghost"
                  }
                  size="sm"
                  data-active={isSelected || isToday}
                  aria-current={isToday ? "date" : undefined}
                  disabled={monthDisabled(monthIndex)}
                  aria-label={format(
                    new Date(year, monthIndex, 1),
                    "MMMM yyyy",
                  )}
                  onClick={() => {
                    changeMonth(new Date(year, monthIndex, 1))
                    setView("day")
                  }}
                >
                  {format(new Date(2000, monthIndex, 1), "MMM")}
                </Button>
              )
            })}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Previous years"
              disabled={yearPageStart <= minYear}
              onClick={() =>
                setYearPageStart((s) => Math.max(minYear, s - yearsPerPage))
              }
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <span className="text-sm font-medium">
              {yearPageStart} – {pageEnd}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Next years"
              disabled={yearPageStart + yearsPerPage > maxYear}
              onClick={() => setYearPageStart((s) => s + yearsPerPage)}
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {Array.from(
              { length: pageEnd - yearPageStart + 1 },
              (_, i) => yearPageStart + i,
            ).map((gridYear) => {
              const isToday = gridYear === today.getFullYear()
              const isSelected =
                selected != null && selected.getFullYear() === gridYear
              return (
                <Button
                  key={gridYear}
                  type="button"
                  variant={
                    isSelected ? "default" : isToday ? "secondary" : "ghost"
                  }
                  size="sm"
                  data-active={isSelected || isToday}
                  aria-current={isToday ? "date" : undefined}
                  aria-label={`${gridYear}`}
                  onClick={() => {
                    changeMonth(new Date(gridYear, displayMonth.getMonth(), 1))
                    setView("month")
                  }}
                >
                  {gridYear}
                </Button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
